import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output, QueryList, ViewChildren} from '@angular/core';
import {CompositeFilterDescriptor, FilterDescriptor, SortDescriptor}                              from '@progress/kendo-data-query';
import {NextSortOperation}                                               from '../../enums/next-sort-operation';
import {FilterType}                                                      from '../../enums/filter-type';
import IGuid = System.IGuid;
import IFieldCropRotation = Data.FieldManagement.IFieldCropRotation;
import ICropTypes = Data.BaseData.ICropTypes;
import {BehaviorSubject}                                                 from 'rxjs';
import {
  ApCropRotationFilterComponent
}                                                                                      from './filter/ap-crop-rotation-filter.component';
import {
  ApKendoIconService
}                                                                                                 from '../../../icons/ap-kendo-icon.service';

const DEFAULT_SORT = 'asc';

/**
 * The Header of the CropRotation Grid
 */
@Component({
  selector: 'ap-crop-rotation-header',
  templateUrl: './ap-crop-rotation-header.component.html',
  styleUrls: ['./ap-crop-rotation-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApCropRotationHeaderComponent {
  protected readonly ApKendoIconService = ApKendoIconService;
  @ViewChildren(ApCropRotationFilterComponent) filterComponents: QueryList<ApCropRotationFilterComponent>;

  /**
   * the current selected Field Ids
   */
  @Input() public selectedFields: IGuid[] = [];
  /**
   * the filtered and sorted ItemView from the Grid
   */
  @Input() public fields: IFieldCropRotation[] = [];
  /**
   * full list ItemView from the Grid
   */
  @Input() public fullItems: IFieldCropRotation[] = [];
  /**
   * the filtered and sorted ItemView from the Grid
   * without paging!
   */
  @Input() public filteredAndSortedItems: IFieldCropRotation[] = [];
  /**
   * the FieldName Translation Text
   */
  @Input() public nameText = '';
  /**
   * the FieldNumber Translation Text
   */
  @Input() public numberText = '';
  /**
   * the FieldSubNumber Translation Text
   */
  @Input() public subNumberText = '';
  /**
   * the AreaHa Translation Text
   */
  @Input() public areaText = '';
  /**
   * the current CampaignYear
   */
  @Input() public currentYear: number;
  /**
   * the Interval of Years to Display
   */
  @Input() public yearInterval: number[];
  /**
   * the Width of one Cell
   */
  @Input() public columnWidth: number;
  /**
   * the Kendo FilterDescriptor that filters the Grid DataSource
   */
  @Input() public filter: CompositeFilterDescriptor;
  /**
   * the available Croptypes for the Farm
   */
  @Input() public availableCropTypes: ICropTypes[];
  @Input() public allCropTypes: ICropTypes[];

  /**
   * fires when the selected Field was changed
   */
  @Output() public selectedFieldsChange = new EventEmitter<IGuid[]>();
  /**
   * fires when the Sorting was changed
   */
  @Output() public sortChange = new EventEmitter<SortDescriptor[]>();
  /**
   * fires when the Kendo Filter was changed
   */
  @Output() public filterChange = new EventEmitter<CompositeFilterDescriptor>();
  /**
   * fires when the Years Interval was changed
   */
  @Output() public yearIntervalChange = new EventEmitter<number[]>();

  public filterTypes = FilterType;

  /**
   * can move the Years View to the left Side
   */
  public get CanScrollLeft(): boolean {
    return this.leftAnchor > -1;
  }

  /**
   * can move the Years View to the right Side
   */
  public get CanScrollRight(): boolean {
    return (this.leftAnchor + this.yearInterval.length + 1) < this.maxIntervals;
  }

  /**
   * the Sort Operation that was processed when the User hits Sort
   */
  public nextSortOperations: { [key: string]: NextSortOperation } = {};

  /**
   * the Kendo SortDescriptor that sorts the DataSource of the Grid
   */
  public sort: SortDescriptor[] = [
    {
      dir: DEFAULT_SORT,
      field: 'FieldName'
    }
  ];

  /**
   * the current Position of the Years View
   */
  public leftAnchor = -1;

  /**
   * the Years Interval Length
   */
  public maxIntervals = 7;

  private static createNewInterval(start: number, length: number): number[] {
    const tmp: number[] = [];
    for (let i = start; i < (length + start); i++) {
      tmp.push(i);
    }
    return tmp;
  }

  /**
   * select all Fields in the DataSource
   */
  public selectAllFields(): void {
    setTimeout(() => {
      if (this.selectedFields.length === this.filteredAndSortedItems.length) {
        this.selectedFieldsChange.emit([]);
      } else {
        this.selectedFieldsChange.emit(this.filteredAndSortedItems.map(x => x.Id));
      }
    }, 1);
  }

  public clearAllFiltersInFilterComponents(): void {
    this.filterComponents?.forEach((filterComponent) => {
      filterComponent.clearFilter();
    });
  }

  /**
   * sort the DataSource by FieldName
   */
  public sortByName(): void {
    this.processSort('FieldName');
  }

  /**
   * sort the DataSource by FieldNumber
   */
  public sortByTNr(): void {
    this.processSort('FieldNumber');
  }

  /**
   * sort the DataSource by FieldSubNumber
   */
  public sortBySubNr(): void {
    this.processSort('FieldSubNumber');
  }

  /**
   * sort the DataSource by AreaHa
   */
  public sortByArea(): void {
    this.processSort('AreaHa');
  }

  /**
   * build the Filter Description for the CropKey Filter
   */
  public createCropKeyFilter: (searchPhrases: any[], year: number, propertyName: string) => FilterDescriptor[] = (searchPhrases, year, propertyName) => {
    // allowing to filter for undefined/empty crops
    const resultFilter = [];
    for (const search of searchPhrases) {
      const matchingCroptypeIds: number[] = search ? this.allCropTypes?.FindAll(_ => _?.[propertyName] === search).map(_ => _.Id) : [0];
      for (const matchingCroptypeId of matchingCroptypeIds) {
        // filter on id '0' if search is undefined(=empty selection)
        if (matchingCroptypeId >= 0) {
          resultFilter.push({
            logic: 'and',
            filters: [
              {
                field: `Yields[${year + 1}].MainCrop`,
                value: matchingCroptypeId,
                operator: 'eq'
              }
            ]
          } as CompositeFilterDescriptor);
        }
      }
    }
    return resultFilter;
  }

  /**
   * moves the Years View into Future
   */
  public onFutureClick(): void {
    this.leftAnchor++;
    const newInterval = ApCropRotationHeaderComponent.createNewInterval(
      this.leftAnchor, this.yearInterval.length);
    this.yearIntervalChange.emit(newInterval);
  }

  /**
   * moves the Years View into Past
   */
  public onBackClick(): void {
    this.leftAnchor--;
    const newInterval = ApCropRotationHeaderComponent.createNewInterval(
      this.leftAnchor, this.yearInterval.length);
    this.yearIntervalChange.emit(newInterval);
  }

  private processSort(field: string): void {
    const sortDescriptor = this.sort.find(i => i.field === field);
    switch (this.detectOperation(sortDescriptor, this.nextSortOperations[field])) {
      case NextSortOperation.SWITCH:
        this.sort = [
          {
            dir: sortDescriptor && sortDescriptor.dir === 'asc' ? 'desc' : DEFAULT_SORT,
            field: sortDescriptor ? sortDescriptor.field : field,
          }
        ];
        this.nextSortOperations[field] = NextSortOperation.OFF;
        break;
      case NextSortOperation.ON:
        this.sort = [
          {
            dir: DEFAULT_SORT,
            field,
          }
        ];
        this.nextSortOperations[field] = NextSortOperation.SWITCH;
        break;
      case NextSortOperation.OFF:
        this.sort = [];
        this.nextSortOperations[field] = NextSortOperation.ON;
        break;
    }
    this.sortChange.emit(this.sort);
  }

  private detectOperation(s: SortDescriptor, op?: NextSortOperation): NextSortOperation {
    if (!op) {
      if (s) {
        return s.dir === DEFAULT_SORT ? NextSortOperation.SWITCH : NextSortOperation.OFF;
      } else {
        return NextSortOperation.ON;
      }
    }
    return op;
  }
}
